import { Card, Row, Col, Space, message } from "antd";
import { AntdButton, DatePickerField, InputField, SelectInput, TextareaField } from "../../../blueplanitUtils/InputField";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getInterviewCandidateDetails, InterviewFeedback, InterviewSchedule } from "../../../Slices/hiringSlice";
import { useEffect, useState } from "react";
import dayjs from "dayjs";

const InterViewScheduling = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const candidate = useSelector((state) => state.hiring.interview);
    const details = useSelector((state) => state.employee.AllEmployees);
    const [questions, setQuestions] = useState([]);
    const [questionFeedback, setQuestionFeedback] = useState({});
    const [interviewId, setInterviewId] = useState(null);
    const [manualQuestions, setManualQuestions] = useState([]);

    const [form, setForm] = useState({
        interviewId:'',
        interviewRound: '',
        mode: '',
        interviewDate: '',
        interviewers: '',
        agenda: '',
        status: '',
        communication: '',
        skill: '',
        attitude: '',
        fit: '',
        comments: '',
        result: ''

    })

    const handleChange = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value
        }))
    }

    useEffect(() => {
        if (id) {
            dispatch(getInterviewCandidateDetails(id))
        }
    }, [dispatch, id])

    useEffect(() => {
        if (candidate?.interviews?.length) {
            const latestInterview = candidate.interviews[candidate.interviews.length - 1];

            setQuestions(latestInterview.interviewQuestionsSnapshot || []);
            setInterviewId(latestInterview.interviewId);
            setForm(prev => ({
                ...prev,
                interviewId: latestInterview.interviewId || '',
                interviewRound: latestInterview.round || '',
                mode: latestInterview.mode || '',
                interviewDate: latestInterview.interviewDate ? dayjs(parseInt(latestInterview.interviewDate)) : '',
                interviewers: latestInterview.interviewers || [],
                agenda: latestInterview.agenda || '',
                status: latestInterview.status || ''
            }));
        }
    }, [candidate]);



    const handleQuestionFeedbackChange = (questionId, key, value) => {
        setQuestionFeedback((prev) => ({
            ...prev,
            [questionId]: {
                ...prev[questionId],
                [key]: value
            }
        }));
    };
    const addManualQuestion = () => {
        const id = `MANUAL-${Date.now()}`;
        setManualQuestions(prev => [
            ...prev,
            {
                questionId: id,
                question: "",
                category: "",
                required: false
            }
        ]);

        setQuestionFeedback(prev => ({
            ...prev,
            [id]: { rating: 0, notes: "" }
        }));
    };

    const updateManualQuestion = (id, key, value) => {
        setManualQuestions(prev =>
            prev.map(q =>
                q.questionId === id ? { ...q, [key]: value } : q
            )
        );
    };

    const deleteManualQuestion = (questionId) => {
        setManualQuestions(prev =>
            prev.filter(q => q.questionId !== questionId)
        );

        setQuestionFeedback(prev => {
            const updated = { ...prev };
            delete updated[questionId];
            return updated;
        });
    };

    const handleScheduleInterview = async () => {
        if (!form.interviewRound || !form.mode || !form.interviewDate) {
            return message.warning("Please fill all required fields!");
        }

        try {
            const payload = {
                candidateId: candidate.candidateId,
                requisitionId: candidate.requisitionId,
                round: form.interviewRound,
                mode: form.mode,
                interviewDate: form.interviewDate.toISOString(),
                interviewers: form.interviewers,
                agenda: form.agenda
            };

            const res = await dispatch(InterviewSchedule(payload)).unwrap();

            setInterviewId(res.interviewId);
            setQuestions(res.interviewQuestionsSnapshot || []);

            message.success("Interview scheduled successfully!");
        } catch (err) {
            console.error(err);
            message.error("Failed to schedule interview");
        }
    };



    const handleSubmitFeedback = async () => {
        try {
            if (!form.result) {
                return message.warning("Select interview result");
            }

            if (!interviewId) {
                return message.warning("Please schedule interview first");
            }

            const allQuestions = [...questions, ...manualQuestions];

            if (!allQuestions.length) {
                return message.warning("Please add at least one question");
            }

            const payload = {
                candidateId: candidate.candidateId, // ✅ REQUIRED
                interviewId,

                communication: Number(form.communication),
                skill: Number(form.skill),
                attitude: Number(form.attitude),
                fit: Number(form.fit),
                comments: form.comments,
                result: form.result,

                questionFeedback: allQuestions.map(q => ({
                    questionId: q.questionId,
                    question: q.question,
                    category: q.category,
                    rating: questionFeedback[q.questionId]?.rating || 0,
                    notes: questionFeedback[q.questionId]?.notes || ""
                }))
            };

            await dispatch(InterviewFeedback(payload)).unwrap();

            message.success("Feedback submitted successfully!");
            navigate(-1);
        } catch (error) {
            console.error(error);
            message.error("Failed to submit feedback");
        }
    };



    const handleBack = () => {
        navigate(-1)
    }

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {id && (
                    <div className="mb-2">
                        <AntdButton
                            label="Close"
                            type="primary"
                            onClick={handleBack}
                        />
                    </div>
                )}
                <Card title="Interview Scheduling & Feedback" style={{ marginBottom: 20 }}>
                    <Card
                        type="inner"
                        title="Candidate Information"
                        style={{ marginBottom: 20 }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col span={5}><b>Candidate ID:</b> <span>{candidate.candidateId}</span></Col>
                            <Col span={4}><b>Name:</b> <span>{candidate.firstName + " " + candidate.lastName || ''}</span></Col>
                            <Col span={5}><b>Applying For:</b> <span>{candidate.jobTitle}</span></Col>
                            <Col span={4}><b>Current Stage:</b> <span>{candidate.stage}</span></Col>
                            <Col span={5}> <b>Email</b> <span>{candidate.email}</span></Col>
                        </Row>
                    </Card>

                    <Card type="inner" title="Schedule Interview" style={{ marginBottom: 20 }}>
                        <Row gutter={24}>
                            <Col span={4}>
                                <InputField
                                    label="Interview ID"
                                    name="interviewId"
                                    value={form.interviewId}
                                    placeholder="INT-0001"
                                    disabled
                                />
                            </Col>

                            <Col span={4}>
                                <SelectInput
                                    label="Interview Round"
                                    name="interviewRound"
                                    style={{ width: '100%' }}
                                    value={form.interviewRound}
                                    onChange={(value) => handleChange('interviewRound', value)}
                                    placeholder="Select interview round"
                                    options={[
                                        { label: "HR Round", value: "HR" },
                                        { label: "Technical Round", value: "Technical" },
                                        { label: "Manager Round", value: "Manager" },
                                        { label: "Final Round", value: "Final" },
                                    ]}
                                //options={["HR", "Technical", "Manager", "Final"]}
                                />
                            </Col>

                            <Col span={4}>
                                <SelectInput
                                    label="Interview Mode"
                                    name="mode"
                                    style={{ width: '100%' }}
                                    value={form.mode}
                                    onChange={(value) => handleChange('mode', value)}
                                    placeholder="Select Interview Mode"
                                    options={[
                                        { label: "In-Person", value: "In-Person" },
                                        { label: "Zoom", value: "Zoom" },
                                        { label: "Microsoft Teams", value: "Teams" },
                                        { label: "Google Meet", value: "Google Meet" },
                                    ]}
                                />
                            </Col>

                            <Col span={4}>
                                <DatePickerField
                                    label="Interview Date & Time"
                                    name="interviewDate"
                                    showTime={true}
                                    format="DD/MM/YYYY h:m"
                                    value={form.interviewDate}
                                    onChange={(value) => handleChange('interviewDate', value)}
                                />
                            </Col>

                            <Col span={4}>
                                <SelectInput
                                    label="Status"
                                    name="status"
                                    style={{ width: '100%' }}
                                    value={form.status}
                                    onChange={(value) => handleChange('status', value)}
                                    placeholder="Select Interview Status"
                                    options={[
                                        { label: "Scheduled", value: "Scheduled" },
                                        { label: "Completed", value: "Completed" }
                                    ]}
                                />
                            </Col>

                            <Col span={4}>
                                <SelectInput
                                    label="Interviewer(s)"
                                    name="interviewers"
                                    style={{ width: '100%' }}
                                    mode="multiple"
                                    value={form.interviewers}
                                    onChange={(value) => handleChange('interviewers', value)}
                                    options={details.map((emp) => ({
                                        label: emp.firstname + " " + emp.lastname,
                                        value: emp.firstname + " " + emp.lastname
                                    }))}
                                    placeholder="Select Interviewers"

                                />
                            </Col>

                            <Col span={24}>
                                <TextareaField
                                    label="Interview Agenda / Notes"
                                    name="agenda"
                                    value={form.agenda}
                                    onChange={(e) => handleChange('agenda', e.target.value)}
                                    rows={3}
                                />
                            </Col>

                            <Col span={24} style={{ textAlign: "right" }}>
                                <Space>
                                    <AntdButton label="Send Interview Details" type="primary" htmlType="submit" onClick={handleScheduleInterview} />
                                    <AntdButton label="Cancel" color="danger" onClick={handleBack} />
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    <Card type="inner" title="Per-Question Feedback" style={{ marginTop: 20 }}>

                        <AntdButton
                            label="Add Interview Question"
                            type="dashed"
                            onClick={addManualQuestion}
                            style={{ marginBottom: 20 }}
                        />

                        {[...questions, ...manualQuestions].map((q) => {
                            const isManual = manualQuestions.some(mq => mq.questionId === q.questionId);

                            return (
                                <Row key={q.questionId} gutter={[16, 16]} style={{ marginBottom: 12 }}>
                                    <Col span={9}>
                                        {isManual ? (
                                            <InputField
                                                label="Interview Question"
                                                value={q.question}
                                                onChange={(e) => updateManualQuestion(q.questionId, "question", e.target.value)}
                                            />
                                        ) : (
                                            <>
                                                <b>{q.category}:</b> {q.question}
                                                {q.required && <span style={{ color: "red" }}> *</span>}
                                            </>
                                        )}
                                    </Col>

                                    <Col span={4}>
                                        {isManual ? (
                                            <InputField
                                                label="Category"
                                                value={q.category}
                                                onChange={(e) => updateManualQuestion(q.questionId, "category", e.target.value)}
                                            />
                                        ) : (
                                            <span>{q.category}</span>
                                        )}
                                    </Col>

                                    <Col span={4}>
                                        <SelectInput
                                            label="Rating"
                                            value={questionFeedback[q.questionId]?.rating || 0}
                                            onChange={(value) =>
                                                handleQuestionFeedbackChange(q.questionId, "rating", Number(value))
                                            }
                                            options={[1, 2, 3, 4, 5].map((n) => ({ label: `${n}`, value: `${n}` }))}
                                        />
                                    </Col>

                                    <Col span={5}>
                                        <InputField
                                            label="Notes"
                                            value={questionFeedback[q.questionId]?.notes || ""}
                                            onChange={(e) =>
                                                handleQuestionFeedbackChange(q.questionId, "notes", e.target.value)
                                            }
                                        />
                                    </Col>

                                    {isManual && (
                                        <Col span={2} style={{ marginTop: 30 }}>
                                            <AntdButton
                                                label="Delete"
                                                color="danger"
                                                onClick={() => deleteManualQuestion(q.questionId)}
                                            />
                                        </Col>
                                    )}
                                </Row>
                            );
                        })}

                        <Row style={{ marginTop: 20 }}>
                            <Col span={6}>
                                <SelectInput
                                    label="Interview Result"
                                    name="result"
                                    value={form.result}
                                    onChange={(value) => handleChange("result", value)}
                                    placeholder="Select Interview Result"
                                    options={[
                                        { label: "Selected", value: "Selected" },
                                        { label: "On Hold", value: "On Hold" },
                                        { label: "Rejected", value: "Rejected" },
                                        { label: "Future Consideration", value: "Future Consideration" },
                                    ]}
                                />
                            </Col>

                            <Col span={24} style={{ textAlign: "right" }}>
                                <Space>
                                    <AntdButton label="Submit Feedback" type="primary" onClick={handleSubmitFeedback} />
                                </Space>
                            </Col>
                        </Row>
                    </Card>


                </Card>
            </div>
        </div>
    )
};

export default InterViewScheduling;